import { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { useNavigate } from '@tanstack/react-router'
import { Check, ShoppingCart, X, Undo2 } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { useTelegram } from '@/shared/lib/telegram'
import { OptimizedImage } from '@/shared/ui/optimized-image'
import { useRemoveFromCart, useCart } from '@/features/cart/api/use-cart'
import { create } from 'zustand'

/**
 * Store для управления уведомлениями о добавлении в корзину
 */
interface CartNotificationState {
  isVisible: boolean
  productName: string | null
  productImage: string | null
  cartItemId: string | null
  canUndo: boolean
  
  show: (data: { productName: string; productImage?: string; cartItemId?: string }) => void
  hide: () => void
  clearUndo: () => void
}

export const useCartNotification = create<CartNotificationState>((set, get) => ({
  isVisible: false,
  productName: null,
  productImage: null,
  cartItemId: null,
  canUndo: true,
  
  show: ({ productName, productImage, cartItemId }) => {
    set({
      isVisible: true,
      productName,
      productImage: productImage || null,
      cartItemId: cartItemId || null,
      canUndo: true,
    })
  },
  
  hide: () => set({ isVisible: false }),
  
  clearUndo: () => set({ canUndo: false }),
}))

/**
 * Виджет: Уведомление о добавлении в корзину
 * 
 * Премиальный баннер с:
 * - Анимацией появления снизу
 * - Кнопкой "Перейти в корзину"
 * - Возможностью отменить (Undo)
 * - Автоскрытие через 4 секунды
 */
export function CartNotification() {
  const navigate = useNavigate()
  const { hapticFeedback } = useTelegram()
  const removeFromCart = useRemoveFromCart()
  const { refetch: refetchCart } = useCart()
  
  const {
    isVisible,
    productName,
    productImage,
    cartItemId,
    canUndo,
    hide,
    clearUndo,
  } = useCartNotification()
  
  const [progress, setProgress] = useState(100)
  const DISPLAY_DURATION = 4000 // 4 секунды
  
  // Автоскрытие с прогресс-баром
  useEffect(() => {
    if (!isVisible) {
      setProgress(100)
      return
    }
    
    const startTime = Date.now()
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime
      const remaining = Math.max(0, 100 - (elapsed / DISPLAY_DURATION) * 100)
      setProgress(remaining)
      
      if (remaining <= 0) {
        hide()
      }
    }, 50)
    
    return () => clearInterval(interval)
  }, [isVisible, hide])
  
  // Обработчик отмены
  const handleUndo = useCallback(async () => {
    if (!cartItemId || !canUndo) return
    
    hapticFeedback.impactOccurred('medium')
    clearUndo()
    
    try {
      await removeFromCart.mutateAsync(cartItemId)
      hapticFeedback.notificationOccurred('success')
      hide()
      refetchCart()
    } catch (error) {
      hapticFeedback.notificationOccurred('error')
    }
  }, [cartItemId, canUndo, hapticFeedback, clearUndo, removeFromCart, hide, refetchCart])
  
  // Переход в корзину
  const handleGoToCart = useCallback(() => {
    hapticFeedback.impactOccurred('light')
    hide()
    navigate({ to: '/cart' })
  }, [hapticFeedback, hide, navigate])
  
  // Закрытие
  const handleClose = useCallback(() => {
    hapticFeedback.impactOccurred('light')
    hide()
  }, [hapticFeedback, hide])
  
  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ 
            type: 'spring',
            damping: 25,
            stiffness: 300,
          }}
          className={cn(
            'fixed left-4 right-4 z-[60]',
            'bg-white dark:bg-dark-card',
            'rounded-2xl shadow-xl dark:shadow-2xl',
            'border border-silk dark:border-dark-border',
            'overflow-hidden',
            'lg:left-auto lg:right-6 lg:max-w-md'
          )}
          style={{
            bottom: 'calc(80px + env(safe-area-inset-bottom, 0px))',
          }}
        >
          {/* Прогресс-бар сверху */}
          <div className="h-1 bg-gray-100 dark:bg-gray-800">
            <motion.div
              className="h-full bg-success"
              initial={{ width: '100%' }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.05, ease: 'linear' }}
            />
          </div>
          
          <div className="p-4">
            <div className="flex items-center gap-3">
              {/* Иконка успеха / Превью товара */}
              <div className="relative flex-shrink-0">
                {productImage ? (
                  <div className="w-12 h-12 rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-800">
                    <OptimizedImage
                      src={productImage}
                      alt=""
                      preset="thumb"
                      wrapperClassName="w-full h-full"
                      className="object-cover"
                      loading="eager"
                      showSkeleton
                    />
                    <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-success rounded-full flex items-center justify-center">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                  </div>
                ) : (
                  <div className="w-12 h-12 rounded-full bg-success/10 dark:bg-success/20 flex items-center justify-center">
                    <Check className="w-6 h-6 text-success" />
                  </div>
                )}
              </div>
              
              {/* Текст */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-ink dark:text-white truncate">
                  Добавлено в корзину
                </p>
                {productName && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {productName}
                  </p>
                )}
              </div>
              
              {/* Кнопки действий */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {/* Кнопка отмены */}
                {canUndo && cartItemId && (
                  <button
                    type="button"
                    onClick={handleUndo}
                    disabled={removeFromCart.isPending}
                    className={cn(
                      'w-9 h-9 rounded-xl flex items-center justify-center',
                      'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400',
                      'hover:bg-gray-200 dark:hover:bg-gray-700',
                      'active:scale-95 transition-all',
                      'disabled:opacity-50'
                    )}
                    aria-label="Отменить"
                  >
                    <Undo2 className="w-4 h-4" />
                  </button>
                )}
                
                {/* Кнопка перехода в корзину */}
                <button
                  type="button"
                  onClick={handleGoToCart}
                  className={cn(
                    'px-4 py-2 rounded-xl flex items-center gap-2',
                    'bg-primary dark:bg-primary-dark text-white',
                    'hover:bg-primary-dark dark:hover:bg-primary-darker',
                    'active:scale-95 transition-all',
                    'text-sm font-medium'
                  )}
                >
                  <ShoppingCart className="w-4 h-4" />
                  <span>В корзину</span>
                </button>
                
                {/* Кнопка закрытия */}
                <button
                  type="button"
                  onClick={handleClose}
                  className={cn(
                    'w-8 h-8 rounded-lg flex items-center justify-center',
                    'text-gray-400 dark:text-gray-500',
                    'hover:bg-gray-100 dark:hover:bg-gray-800',
                    'active:scale-95 transition-all'
                  )}
                  aria-label="Закрыть"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}




